import { useState } from "react";
import { motion } from "framer-motion";
import { Plus, Flame, Clock, Play, Download, Loader2, Share2 } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { downloadAsset } from "@/lib/download";
import { shareProduct } from "@/lib/share";
import { formatINR } from "@/config";

export default function ProductCard({ product, index, onOpen }) {
  const { addItem } = useCart();
  const [downloading, setDownloading] = useState(false);
  const comingSoon = product.is_live === false;

  const download = async () => {
    if (downloading) return;
    setDownloading(true);
    await downloadAsset(`/products/${product.id}/download`, `${product.sku || "product"}-varsha.png`, "product image");
    setDownloading(false);
  };

  return (
    <motion.article
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: (index % 3) * 0.1, ease: [0.16, 1, 0.3, 1] }}
      data-testid={`product-card-${product.id}`}
      className="group relative overflow-hidden rounded-2xl border border-white/10 bg-[#181B24] transition-colors duration-300 hover:border-cyan-400/40"
    >
      <button
        onClick={() => onOpen(product)}
        data-testid={`product-open-${product.id}`}
        className="block w-full cursor-pointer overflow-hidden text-left"
      >
        <div className="relative aspect-[4/5] overflow-hidden bg-[#0F1118]">
          {comingSoon ? (
            <div data-testid={`product-coming-soon-panel-${product.id}`} className="relative flex h-full w-full flex-col items-center justify-center overflow-hidden bg-[#0C0E14]">
              <div className="absolute inset-0 opacity-[0.18] [background-image:repeating-linear-gradient(135deg,#22d3ee_0,#22d3ee_1px,transparent_1px,transparent_11px)]" />
              <div className="absolute -left-10 top-1/2 h-40 w-40 -translate-y-1/2 rounded-full bg-fuchsia-600/25 blur-3xl" />
              <div className="absolute -right-10 top-1/3 h-40 w-40 rounded-full bg-cyan-500/25 blur-3xl" />
              <span className="relative font-display text-xl font-bold uppercase leading-none tracking-tight text-transparent sm:text-3xl [background-image:linear-gradient(100deg,#22d3ee,#e879f9,#facc15)] [-webkit-background-clip:text] [background-clip:text]">
                Coming
              </span>
              <span className="relative font-display text-xl font-bold uppercase leading-none tracking-tight text-transparent sm:text-3xl [background-image:linear-gradient(100deg,#facc15,#e879f9,#22d3ee)] [-webkit-background-clip:text] [background-clip:text]">
                Soon
              </span>
              {product.sku && (
                <span className="relative mt-4 rounded-full border border-white/15 bg-[#0A0B0E]/70 px-3 py-1 font-mono text-[11px] uppercase tracking-[0.2em] text-cyan-300 backdrop-blur" data-testid={`product-code-${product.id}`}>
                  Code {product.sku}
                </span>
              )}
              <span className="relative mt-2 font-display text-lg font-bold text-slate-100">{formatINR(product.price)}</span>
              <span className="relative mt-3 font-mono text-[9px] uppercase tracking-[0.25em] text-slate-500">Ask us for launch date</span>
            </div>
          ) : (
            <>
              <img
                src={product.image || "/placeholder-product.svg"}
                alt={product.name}
                loading="lazy"
                onError={(e) => { e.currentTarget.src = "/placeholder-product.svg"; }}
                className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.07]"
              />
              {product.images?.length > 1 && (
                <img
                  src={product.images[1]}
                  alt=""
                  loading="lazy"
                  data-testid={`product-hover-image-${product.id}`}
                  className="absolute inset-0 h-full w-full object-cover opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                />
              )}
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-[#181B24] to-transparent" />
            </>
          )}
          {product.offer_percent != null && (
            <span data-testid={`product-offer-badge-${product.id}`} className="absolute left-2 top-2 rounded-full bg-gradient-to-r from-[#B1121F] to-[#E0A52E] px-2 py-0.5 font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-[#FFE9B0] sm:left-3 sm:top-3 sm:px-3 sm:py-1 sm:text-[10px]">
              {Math.round(product.offer_percent)}% Off
            </span>
          )}
          {product.is_new_launch && (
            <span data-testid={`product-new-badge-${product.id}`} className="absolute left-2 top-9 rounded-full bg-yellow-300 px-2 py-0.5 font-mono text-[8px] font-bold uppercase tracking-[0.12em] text-[#0A0B0E] sm:left-3 sm:top-12 sm:px-3 sm:py-1 sm:text-[10px]">
              New Launch
            </span>
          )}
          {product.videos?.length > 0 && !comingSoon && (
            <span data-testid={`product-video-badge-${product.id}`} className="absolute bottom-2 left-2 flex items-center gap-1 rounded-full bg-[#0A0B0E]/80 px-2 py-0.5 font-mono text-[8px] uppercase tracking-[0.12em] text-cyan-300 backdrop-blur sm:bottom-3 sm:left-3 sm:gap-1.5 sm:px-3 sm:py-1 sm:text-[10px]">
              <Play size={9} className="fill-cyan-300" /> Video
            </span>
          )}
          {product.bulk_price && !comingSoon && (
            <span className="absolute left-2 top-2 rounded-full bg-[#0A0B0E]/80 px-2 py-0.5 font-mono text-[8px] uppercase tracking-[0.12em] text-yellow-300 backdrop-blur sm:left-3 sm:top-3 sm:px-3 sm:py-1 sm:text-[10px]">
              Bulk {formatINR(product.bulk_price)} / {product.bulk_min}+
            </span>
          )}
          <span className={`absolute right-2 top-2 rounded-full px-2 py-0.5 font-mono text-[8px] uppercase tracking-[0.12em] backdrop-blur sm:right-3 sm:top-3 sm:px-3 sm:py-1 sm:text-[10px] ${comingSoon ? "bg-yellow-300/15 text-yellow-300" : product.stock > 0 ? "bg-cyan-400/10 text-cyan-300" : "bg-red-500/15 text-red-400"}`} data-testid={`product-stock-${product.id}`}>
            {comingSoon ? "Coming soon" : product.stock > 0 ? "In stock" : "Sold out"}
          </span>
        </div>
        <div className="p-3 sm:p-5">
          <div className="flex items-center justify-between gap-2">
            <div className="truncate font-mono text-[9px] uppercase tracking-[0.15em] text-fuchsia-400 sm:text-[10px] sm:tracking-[0.2em]">{product.category}</div>
            {product.sku && (
              <span data-testid={`product-code-badge-${product.id}`} className="shrink-0 rounded-md border border-cyan-400/25 bg-cyan-400/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.15em] text-cyan-300">
                {product.sku}
              </span>
            )}
          </div>
          <h3 className="mt-1.5 line-clamp-2 font-display text-sm font-semibold leading-snug tracking-normal text-slate-100 sm:text-lg">{product.name}</h3>
          <div className="mt-2 hidden flex-wrap gap-1.5 sm:flex">
            {product.substrate.map((s) => (
              <span key={s} className="rounded-full border border-white/10 px-2.5 py-0.5 text-[10px] text-slate-400">{s}</span>
            ))}
          </div>
          <div className="mt-3 flex items-center justify-between gap-1 sm:mt-4">
            <div className="font-display text-base font-bold text-slate-50 sm:text-xl">
              {product.offer_price != null ? (
                <span data-testid={`product-offer-price-${product.id}`}>
                  <span className="text-[#F5C45A]">{formatINR(product.offer_price)}</span>
                  <span className="ml-1.5 font-mono text-[12px] font-normal text-slate-400 line-through">{formatINR(product.price)}</span>
                </span>
              ) : (
                formatINR(product.price)
              )}
              <span className="ml-1.5 font-mono text-[10px] font-normal uppercase tracking-wider text-slate-500">per pc</span>
            </div>
            <span className="hidden items-center gap-1 font-mono text-[10px] uppercase tracking-wider text-slate-500 sm:flex">
              <Flame size={11} className="text-yellow-400" /> {product.press_temp.split(" ")[0]}
            </span>
          </div>
        </div>
      </button>
      <div className="flex items-center gap-2 px-3 pb-3 sm:px-5 sm:pb-5">
        <button
          data-testid={`add-to-order-${product.id}`}          disabled={comingSoon || product.stock <= 0}
          onClick={() => product.size_options?.length ? onOpen(product) : addItem(product)}
          className="flex h-11 w-full items-center justify-center gap-1.5 rounded-xl border border-cyan-400/30 text-xs font-semibold text-cyan-300 sm:gap-2 sm:text-sm transition-all duration-300 hover:bg-cyan-400 hover:text-[#0A0B0E] disabled:cursor-not-allowed disabled:opacity-30"
        >
          {comingSoon ? <><Clock size={14} /> <span className="hidden sm:inline">Coming Soon</span><span className="sm:hidden">Soon</span></> : <><Plus size={14} /> <span className="hidden sm:inline">{product.size_options?.length ? "Choose sizes" : "Add to Order"}</span><span className="sm:hidden">{product.size_options?.length ? "Sizes" : "Add"}</span></>}
        </button>
        {!comingSoon && (
          <button
            data-testid={`product-download-${product.id}`}
            onClick={download}
            disabled={downloading}
            title="Download image with details"
            aria-label="Download image with details"
            className="flex h-11 w-10 shrink-0 items-center justify-center rounded-xl sm:w-11 border border-white/15 text-slate-400 transition-colors duration-300 hover:border-fuchsia-400/50 hover:text-fuchsia-300 disabled:opacity-40"
          >
            {downloading ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
          </button>
        )}
        {!comingSoon && (
          <button
            data-testid={`product-share-${product.id}`}
            onClick={() => shareProduct(product)}
            title="Share this product"
            aria-label="Share this product"
            className="flex h-11 w-10 shrink-0 items-center justify-center rounded-xl sm:w-11 border border-white/15 text-slate-400 transition-colors duration-300 hover:border-cyan-400/50 hover:text-cyan-300"
          >
            <Share2 size={15} />
          </button>
        )}
      </div>
    </motion.article>
  );
}
